import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  GitBranch,
  Shield,
  List,
  Plus,
  Trash2,
  X,
  Save,
  Settings,
  DollarSign,
} from "lucide-react";
import {
  Pipeline,
  PipelinePermission,
  Stage,
  isMainPipeline,
} from "@/types/pipeline";
import { Role } from "@/types/roles";
import pipelineAPI from "@/services/pipelineService";
import { rolesService } from "@/services/roles";
import { opportunityAPI } from "@/services/opportunityService";
import { CreateStageModal } from "@/components/opportunities/CreateStageModal";
import { useToast } from "@/hooks/use-toast";
import { RootState } from "@/store";

interface PipelineDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pipeline: Pipeline | null;
  onPipelineUpdated?: () => void;
}

export function PipelineDetailsModal({
  isOpen,
  onClose,
  pipeline,
  onPipelineUpdated,
}: PipelineDetailsModalProps) {
  const { toast } = useToast();
  const company = useSelector((state: RootState) => state.auth.company);
  const companySlug = company?.slug || "";

  const [activeTab, setActiveTab] = useState<
    "general" | "permissions" | "stages"
  >("general");
  const [permissions, setPermissions] = useState<PipelinePermission[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);

  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedIncomeObjective, setEditedIncomeObjective] = useState<
    number | undefined
  >(undefined);
  const [editedIsDefault, setEditedIsDefault] = useState(false);

  useEffect(() => {
    if (isOpen && pipeline) {
      setActiveTab("general");
      setEditedName(pipeline.name);
      setEditedDescription(pipeline.description || "");
      setEditedIncomeObjective(pipeline.income_objective);
      setEditedIsDefault(pipeline.is_default || false);
      loadPipelineData();
      loadRoles();
    }
  }, [isOpen, pipeline]);

  const loadRoles = async () => {
    try {
      const response = await rolesService.getRoles(companySlug);
      if (response.data) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error("Error al cargar roles:", error);
    }
  };

  const loadPipelineData = async () => {
    if (!pipeline || !companySlug) return;

    setIsLoading(true);
    try {
      const pipelineResponse = await pipelineAPI.getPipelineById(
        companySlug,
        pipeline.id,
      );

      const stagesResponse = await opportunityAPI.getStages({
        slug: companySlug,
        pipeline_id: pipeline.id,
        is_active: true,
      });

      if (pipelineResponse.success && pipelineResponse.data) {
        setPermissions(pipelineResponse.data.permissions || []);
      }

      if (stagesResponse.data) {
        const filteredStages = stagesResponse.data.filter(
          (s: any) => s.pipeline_id === pipeline.id,
        );

        setStages(
          filteredStages.sort(
            (a: any, b: any) => a.order_position - b.order_position,
          ) as unknown as Stage[],
        );
      }
    } catch (error) {
      console.error("Error al cargar detalles del pipeline:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los detalles del pipeline",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    if (!pipeline || isPrincipal) return;

    setIsSaving(true);
    try {
      await pipelineAPI.updatePipeline(companySlug, pipeline.id, {
        name: editedName,
        description: editedDescription,
        income_objective: editedIncomeObjective,
        is_default: editedIsDefault,
      });

      toast({
        title: "Pipeline actualizado",
        description: "Los cambios se han guardado correctamente",
      });

      onPipelineUpdated?.();
    } catch (error: any) {
      console.error("Error al actualizar pipeline:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "No se pudo actualizar el pipeline",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPermission = async (roleId: string) => {
    if (!pipeline) return;

    try {
      await pipelineAPI.createPipelinePermission(companySlug, {
        pipeline_id: pipeline.id,
        role_id: roleId,
        can_view: true,
        can_edit: false,
        can_delete: false,
      });

      toast({
        title: "Permiso agregado",
        description: "El permiso se ha agregado correctamente",
      });

      loadPipelineData();
    } catch (error: any) {
      console.error("Error al agregar permiso:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "No se pudo agregar el permiso",
        variant: "destructive",
      });
    }
  };

  const handleDeletePermission = async (permissionId: string) => {
    if (!pipeline) return;

    try {
      await pipelineAPI.deletePipelinePermission(
        companySlug,
        pipeline.id,
        permissionId,
      );

      toast({
        title: "Permiso eliminado",
        description: "El permiso se ha eliminado correctamente",
      });

      loadPipelineData();
    } catch (error: any) {
      console.error("Error al eliminar permiso:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "No se pudo eliminar el permiso",
        variant: "destructive",
      });
    }
  };

  const handleTogglePermission = async (
    permissionId: string,
    field: "can_view" | "can_edit" | "can_delete",
    currentValue: boolean,
  ) => {
    if (!pipeline) return;

    const permission = permissions.find((p) => p.id === permissionId);
    if (!permission) return;

    try {
      await pipelineAPI.updatePipelinePermission(
        companySlug,
        pipeline.id,
        permissionId,
        {
          ...permission,
          [field]: !currentValue,
        },
      );

      setPermissions(
        permissions.map((p) =>
          p.id === permissionId ? { ...p, [field]: !currentValue } : p,
        ),
      );

      toast({
        title: "Permiso actualizado",
        description: "El permiso se ha actualizado correctamente",
      });
    } catch (error: any) {
      console.error("Error al actualizar permiso:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "No se pudo actualizar el permiso",
        variant: "destructive",
      });
    }
  };

  if (!pipeline) return null;

  const isPrincipal = isMainPipeline(pipeline);

  const availableRoles = roles.filter(
    (role) => !permissions.some((p) => p.role_id === role.id),
  );

  const getStageTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      start: "Inicial",
      progress: "En Progreso",
      end: "Final",
    };
    return types[type] || type;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="flex items-center justify-between text-xl">
              <div className="flex items-center gap-2">
                <GitBranch className="h-5 w-5 text-blue-600" />
                {pipeline.name}
              </div>
              <div className="flex gap-2">
                {isPrincipal && (
                  <Badge className="bg-purple-100 text-purple-800">
                    Pipeline Principal
                  </Badge>
                )}
                {pipeline.is_default && (
                  <Badge className="bg-blue-100 text-blue-800">
                    Por Defecto
                  </Badge>
                )}
              </div>
            </DialogTitle>
            <DialogDescription>
              Administra la configuración, etapas y permisos de este pipeline.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              <div className="flex border-b px-6 bg-slate-50/40">
                <button
                  onClick={() => setActiveTab("general")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "general"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <Settings className="h-4 w-4" />
                  General
                </button>
                <button
                  onClick={() => setActiveTab("stages")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "stages"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <List className="h-4 w-4" />
                  Etapas ({stages.length})
                </button>
                <button
                  onClick={() => setActiveTab("permissions")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "permissions"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Permisos ({permissions.length})
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {activeTab === "general" && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 border-b pb-2">
                      <Settings className="h-4 w-4 text-slate-500" />
                      <h3 className="text-sm font-medium text-slate-900">
                        Información General
                      </h3>
                    </div>

                    {isPrincipal && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-purple-900">
                              Pipeline Principal
                            </h4>
                            <p className="text-sm text-purple-700 mt-1">
                              Este es el pipeline principal del sistema y no se
                              puede editar.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium">
                          Nombre del Pipeline
                        </Label>
                        <Input
                          id="name"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          disabled={isPrincipal}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="income_objective"
                          className="text-sm font-medium"
                        >
                          Meta de Ventas
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                            <DollarSign className="h-4 w-4" />
                          </span>
                          <Input
                            id="income_objective"
                            type="number"
                            value={editedIncomeObjective || ""}
                            onChange={(e) =>
                              setEditedIncomeObjective(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            disabled={isPrincipal}
                            className="pl-9"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="description"
                        className="text-sm font-medium"
                      >
                        Descripción
                      </Label>
                      <Textarea
                        id="description"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        disabled={isPrincipal}
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50 hover:bg-slate-50 transition-colors">
                      <div className="space-y-0.5">
                        <Label
                          htmlFor="is_default"
                          className="text-base font-medium cursor-pointer"
                        >
                          Pipeline por Defecto
                        </Label>
                        <p className="text-xs text-slate-500">
                          Se selecciona automáticamente al crear oportunidades
                        </p>
                      </div>
                      <Switch
                        id="is_default"
                        checked={editedIsDefault}
                        onCheckedChange={setEditedIsDefault}
                        disabled={isPrincipal}
                      />
                    </div>

                    {!isPrincipal && (
                      <div className="flex justify-end pt-4">
                        <Button
                          onClick={handleSaveGeneral}
                          disabled={isSaving}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Guardar Cambios
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "stages" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 border-b pb-2 w-full mr-4">
                        <List className="h-4 w-4 text-slate-500" />
                        <h3 className="text-sm font-medium text-slate-900">
                          Etapas del Pipeline
                        </h3>
                      </div>
                      <Button
                        onClick={() => setIsStageModalOpen(true)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Gestionar Etapas
                      </Button>
                    </div>

                    {stages.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed rounded-lg">
                        <List className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500 mb-4">
                          No hay etapas configuradas en este pipeline
                        </p>
                        <Button
                          onClick={() => setIsStageModalOpen(true)}
                          variant="outline"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Agregar Primera Etapa
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {stages
                          .sort((a, b) => a.order_position - b.order_position)
                          .map((stage, index) => (
                            <div
                              key={stage.id}
                              className="flex items-start gap-4 border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 text-slate-600 font-bold flex-shrink-0">
                                {index + 1}
                              </div>
                              <div
                                className="h-10 w-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: stage.color }}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium">{stage.name}</h4>
                                  <Badge variant="outline" className="text-xs">
                                    {getStageTypeLabel(stage.stage_type)}
                                  </Badge>
                                </div>
                                {stage.description && (
                                  <p className="text-sm text-slate-600">
                                    {stage.description}
                                  </p>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                  Posición: {stage.order_position}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "permissions" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b pb-2">
                      <Shield className="h-4 w-4 text-slate-500" />
                      <h3 className="text-sm font-medium text-slate-900">
                        Acceso y Permisos
                      </h3>
                    </div>

                    {isPrincipal ? (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-purple-900">
                              Pipeline Principal
                            </h4>
                            <p className="text-sm text-purple-700 mt-1">
                              Los permisos no se pueden editar ya que está
                              disponible para todos los usuarios.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {availableRoles.length > 0 && (
                          <div className="flex items-end gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                            <div className="flex-1 space-y-1">
                              <Label
                                htmlFor="add-role"
                                className="text-xs font-medium text-slate-700"
                              >
                                Agregar Rol
                              </Label>
                              <Select onValueChange={handleAddPermission}>
                                <SelectTrigger className="w-full bg-white">
                                  <SelectValue placeholder="Seleccionar rol..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableRoles.map((role) => (
                                    <SelectItem key={role.id} value={role.id}>
                                      {role.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="pb-1 text-xs text-slate-500">
                              Selecciona un rol para otorgarle acceso.
                            </div>
                          </div>
                        )}

                        <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                          <div className="grid grid-cols-12 gap-4 p-3 bg-slate-50/80 border-b text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            <div className="col-span-4 pl-2">Rol</div>
                            <div className="col-span-6 grid grid-cols-3 gap-2 text-center">
                              <span>Ver</span>
                              <span>Editar</span>
                              <span>Eliminar</span>
                            </div>
                            <div className="col-span-2 text-right pr-2">
                              Acciones
                            </div>
                          </div>

                          <div className="max-h-[400px] overflow-y-auto">
                            {permissions.length === 0 ? (
                              <div className="p-8 text-center text-slate-500 text-sm">
                                No hay permisos asignados.
                              </div>
                            ) : (
                              permissions.map((permission) => (
                                <div
                                  key={permission.id}
                                  className="grid grid-cols-12 gap-4 p-3 items-center border-b last:border-0 hover:bg-slate-50 transition-colors"
                                >
                                  <div className="col-span-4 font-medium text-sm text-slate-900 pl-2">
                                    {permission.role?.name || "Rol Desconocido"}
                                  </div>
                                  <div className="col-span-6 grid grid-cols-3 gap-2 justify-items-center">
                                    <Switch
                                      checked={permission.can_view}
                                      onCheckedChange={() =>
                                        handleTogglePermission(
                                          permission.id,
                                          "can_view",
                                          permission.can_view,
                                        )
                                      }
                                      disabled={isPrincipal}
                                      className="scale-75"
                                    />
                                    <Switch
                                      checked={permission.can_edit}
                                      onCheckedChange={() =>
                                        handleTogglePermission(
                                          permission.id,
                                          "can_edit",
                                          permission.can_edit,
                                        )
                                      }
                                      disabled={isPrincipal}
                                      className="scale-75"
                                    />
                                    <Switch
                                      checked={permission.can_delete}
                                      onCheckedChange={() =>
                                        handleTogglePermission(
                                          permission.id,
                                          "can_delete",
                                          permission.can_delete,
                                        )
                                      }
                                      disabled={isPrincipal}
                                      className="scale-75"
                                    />
                                  </div>
                                  <div className="col-span-2 text-right pr-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleDeletePermission(permission.id)
                                      }
                                      disabled={isPrincipal}
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={onClose}>
                  <X className="h-4 w-4 mr-2" />
                  Cerrar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CreateStageModal
        isOpen={isStageModalOpen}
        onClose={() => setIsStageModalOpen(false)}
        slug={companySlug}
        pipelineId={pipeline?.id}
        onStageCreated={() => {
          console.log("🔍 Etapa creada, recargando pipeline...");
          loadPipelineData();
          onPipelineUpdated?.();
        }}
        onStageUpdated={() => {
          loadPipelineData();
          onPipelineUpdated?.();
        }}
        onStageDeleted={() => {
          loadPipelineData();
          onPipelineUpdated?.();
        }}
      />
    </>
  );
}
